/**
 * BasketSyncProvider
 *
 * A React component that provides real-time basket synchronization
 * across multiple tabs, devices, and team members.
 *
 * This provider should be placed near the top of your component tree,
 * typically wrapping the main app content.
 *
 * Usage:
 * <BasketSyncProvider>
 *   <YourApp />
 * </BasketSyncProvider>
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  createContext,
  useContext,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { notification } from 'antd'
import useBasketSocket from 'src/utils/useBasketSocket'
import { updateOrder, setBasket } from 'src/features/OrdersSlice'
import {
  BASKET_SYNC_REMOVAL_NOTIFICATION_KEY,
  createDebouncedRemovalNotifier,
  getRemovalToastDescription,
} from 'src/utils/basketSyncNotifications'

// Context for basket sync state
const BasketSyncContext = createContext({
  isConnected: false,
  lastEvent: null,
})

export const useBasketSync = () => useContext(BasketSyncContext)

const BasketSyncProvider = ({ children }) => {
  const dispatch = useDispatch()
  const venue = useSelector(state => state.venue.venue)
  const account = useSelector(state => state.auth.account)

  const venueId = venue?._id
  const userId = account?._id

  const removalNotifierRef = useRef(null)

  useEffect(() => {
    removalNotifierRef.current = createDebouncedRemovalNotifier({
      showNotification: count => {
        notification.info({
          key: BASKET_SYNC_REMOVAL_NOTIFICATION_KEY,
          message: 'Basket Updated',
          description: getRemovalToastDescription(count),
          duration: 3,
          placement: 'bottomRight',
        })
      },
    })

    return () => removalNotifierRef.current?.cancelPending()
  }, [])

  // Handle item updated - directly update Redux state
  const handleBasketUpdate = useCallback(
    data => {
      if (data?.order) {
        console.log('[BasketSync] Updating order in state:', data.order._id)
        dispatch(updateOrder(data.order))
      }
    },
    [dispatch],
  )

  // Handle item added - directly add to Redux state
  const handleItemAdded = useCallback(
    data => {
      if (data?.order) {
        console.log('[BasketSync] Adding order to state:', data.order._id)
        dispatch(updateOrder(data.order))
        notification.info({
          message: 'Basket Updated',
          description: 'A new item was added to the basket.',
          duration: 3,
          placement: 'bottomRight',
        })
      }
    },
    [dispatch],
  )

  // Handle item removed - directly remove from Redux state
  const handleItemRemoved = useCallback(
    data => {
      if (data?.orderId) {
        console.log('[BasketSync] Removing order from state:', data.orderId)
        // Setting quantity to 0 triggers removal in the reducer
        dispatch(updateOrder({ _id: data.orderId, quantity: 0 }))
        removalNotifierRef.current?.queueRemoval()
      }
    },
    [dispatch],
  )

  // Handle basket reset
  const handleBasketReset = useCallback(() => {
    dispatch(setBasket([]))
    notification.warning({
      message: 'Basket Cleared',
      description: 'The basket has been cleared.',
      duration: 3,
      placement: 'bottomRight',
    })
  }, [dispatch])

  // Handle order confirmed
  const handleOrderConfirmed = useCallback(data => {
    const supplierCount = data?.confirmedSuppliers?.length || 0
    notification.success({
      message: 'Orders Sent',
      description: `Orders were sent to ${supplierCount} supplier${
        supplierCount !== 1 ? 's' : ''
      }. The basket will be updated.`,
      duration: 5,
      placement: 'bottomRight',
    })
  }, [])

  // Initialize basket socket
  const { isConnected, lastEvent } = useBasketSocket({
    venueId,
    userId,
    onBasketUpdate: handleBasketUpdate,
    onItemAdded: handleItemAdded,
    onItemUpdated: handleBasketUpdate, // Use same handler as basket update
    onItemRemoved: handleItemRemoved,
    onBasketReset: handleBasketReset,
    onOrderConfirmed: handleOrderConfirmed,
    enabled: !!venueId,
  })

  // Log connection status changes
  useEffect(() => {
    if (isConnected) {
      console.log('[BasketSync] Connected to real-time basket updates')
    } else if (venueId) {
      console.log('[BasketSync] Disconnected from real-time basket updates')
    }
  }, [isConnected, venueId])

  const contextValue = {
    isConnected,
    lastEvent,
  }

  return (
    <BasketSyncContext.Provider value={contextValue}>
      {children}
    </BasketSyncContext.Provider>
  )
}

export default BasketSyncProvider
